import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {promptAndGenerateLLMReportFromStudy} from 'studies/report-generation/promptAndGenerateLLMReport';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {getInitialAnalysisNameForStudy} from 'studies/typed/utils/analysis/analysisUtils';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import PlaceholderText from 'ui-library/PlaceholderText';
import runModal from 'ui-library/runModal';

import {StudyOverviewViewQuery} from './__generated/StudyOverviewViewQuery';
import StudyAnalysisListView from './StudyAnalysisListView';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;

interface StudyReportOverviewProps {
  study: StudyNode;
}

async function handleGenerateLLMReport(studyId: string): Promise<void> {
  const newEntryId = await promptAndGenerateLLMReportFromStudy(studyId);
  if (newEntryId) {
    FileBrowserActions.openItem(newEntryId);
  }
}

/**
 * Provides quick access to creating and reviewing study reports.
 */
export default function StudyReviewOverview({study}: StudyReportOverviewProps): JSX.Element {
  if (!study.permissions.writable) {
    return <PlaceholderText value="Reports cannot be assigned to a study that is readonly." />;
  }
  const adapter = getTypedStudyAdapter(study.studySchema.studyType);
  const {analysis} = adapter.review;

  const analysisPropsData = analysis?.useCreateAnalysisModalProps?.(
    study.id,
    ActionOrigin.STUDY_OVERVIEW_PAGE
  );
  const initialAnalysisName = getInitialAnalysisNameForStudy(study.name);

  return (
    <>
      <div className={css(styles.analyses)}>
        {/* Study Analysis */}
        {BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS && analysisPropsData && (
          <>
            <FormGroup>
              <div>
                <FieldLabel label="Study analysis" />
              </div>
              <div>
                <Tooltip
                  text={analysisPropsData.noPropsReason ?? null}
                  isDisabled={analysisPropsData.noPropsReason == null}
                >
                  <Button
                    dataTestAttrs={{
                      component: 'StudyReviewOverview',
                      element: 'generateAnalysisButton',
                    }}
                    size={ButtonSize.MEDIUM}
                    autoHeight={true}
                    buttonStyle={ButtonStyle.TERTIARY_BLUE}
                    text="Generate analysis"
                    icon={ModelTypeConstants.ANALYSIS}
                    {...(analysisPropsData.noPropsReason !== undefined
                      ? {disabled: true}
                      : {
                          onClick: () => {
                            runModal(CreateAnalysisModal, {
                              ...analysisPropsData.props(),
                              initialAnalysisName,
                              hideDataSharingWarningBanner: true,
                              title: 'Generate analysis',
                              ...analysisPropsData.props(),
                            });
                          },
                        })}
                  />
                </Tooltip>
              </div>
            </FormGroup>
            <div className={css(styles.analyisListContainer)}>
              <StudyAnalysisListView studyId={study.id} />
            </div>
          </>
        )}
      </div>
      {/* Study Report Generation */}
      <div>
        {adapter.review.llm.enabled && BenchlingConfig.STUDIES_ENABLE_EXPERIMENTAL_LLM_REPORT_GENERATION && (
          <Button
            size={ButtonSize.MEDIUM}
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            text="Generate study report"
            icon={FolderItemIcon.Entry}
            onClick={() => {
              handleGenerateLLMReport(study.id);
            }}
          />
        )}
      </div>
      {/* Study Report Review TODO */}
    </>
  );
}

const styles = StyleSheet.create({
  analyses: {
    display: 'flex',
    flexDirection: 'row',
  },
  analyisListContainer: {
    marginLeft: '60px',
  },
});
